import { useState, useEffect } from 'react';
import { performanceMonitor, getPerformanceGrade } from '../../utils/performance/webVitals';
import type { PerformanceMetrics } from '../../utils/performance/webVitals';

export function PerformanceDashboard() {
  const [metrics, setMetrics] = useState<PerformanceMetrics>({});
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Subscribe to performance metrics updates
    const unsubscribe = performanceMonitor.subscribe(setMetrics);
    
    // Get initial metrics
    setMetrics(performanceMonitor.getMetrics());

    // Show dashboard only in development or when explicitly enabled
    const showDashboard = process.env.NODE_ENV === 'development' || 
                         localStorage.getItem('show-perf-dashboard') === 'true';
    setIsVisible(showDashboard);

    return unsubscribe;
  }, []);

  if (!isVisible) return null;

  const grades = getPerformanceGrade(metrics);

  const getGradeColor = (grade: string) => {
    switch (grade) {
      case 'good': return 'text-green-600 bg-green-50';
      case 'needs-improvement': return 'text-yellow-600 bg-yellow-50';
      case 'poor': return 'text-red-600 bg-red-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const formatMetric = (value: number | undefined, unit: string) => {
    if (value === undefined) return 'N/A';
    return `${value.toFixed(2)}${unit}`;
  };

  return (
    <div className="fixed bottom-4 right-4 bg-white border border-gray-200 rounded-lg shadow-lg p-4 max-w-sm z-50">
      <div className="flex justify-between items-center mb-3">
        <h3 className="text-sm font-semibold text-gray-900">Performance Dashboard</h3>
        <button
          onClick={() => setIsVisible(false)}
          className="text-gray-400 hover:text-gray-600"
          aria-label="Close performance dashboard"
        >
          ×
        </button>
      </div>

      <div className="space-y-2 text-xs">
        <div className={`flex justify-between p-2 rounded ${getGradeColor(grades.lcp)}`}>
          <span>LCP:</span>
          <span>{formatMetric(metrics.lcp, 'ms')}</span>
        </div>

        <div className={`flex justify-between p-2 rounded ${getGradeColor(grades.inp)}`}>
          <span>INP:</span>
          <span>{formatMetric(metrics.inp, 'ms')}</span>
        </div>

        <div className={`flex justify-between p-2 rounded ${getGradeColor(grades.cls)}`}>
          <span>CLS:</span>
          <span>{formatMetric(metrics.cls, '')}</span>
        </div>

        <div className="flex justify-between p-2 rounded bg-gray-50">
          <span>FCP:</span>
          <span>{formatMetric(metrics.fcp, 'ms')}</span>
        </div>

        <div className="flex justify-between p-2 rounded bg-gray-50">
          <span>TTFB:</span>
          <span>{formatMetric(metrics.ttfb, 'ms')}</span>
        </div>
      </div>

      <div className={`mt-3 p-2 rounded text-center text-xs font-medium ${getGradeColor(grades.overall)}`}>
        Overall: {grades.overall.toUpperCase()}
      </div>

      <div className="mt-2 text-xs text-gray-500 text-center">
        <button
          onClick={() => console.table(metrics)}
          className="hover:text-gray-700 underline"
        >
          Log to Console
        </button>
      </div>
    </div>
  );
}

// Development helper component
export function PerformanceToggle() {
  const [showDashboard, setShowDashboard] = useState(
    localStorage.getItem('show-perf-dashboard') === 'true'
  );

  const toggleDashboard = () => {
    const newValue = !showDashboard;
    setShowDashboard(newValue);
    localStorage.setItem('show-perf-dashboard', newValue.toString());
    // Refresh to show/hide dashboard
    window.location.reload();
  };

  // Only show in development
  if (process.env.NODE_ENV !== 'development') return null;

  return (
    <button
      onClick={toggleDashboard}
      className="fixed bottom-4 left-4 bg-gray-800 text-white px-3 py-2 rounded-lg text-xs z-50"
      title="Toggle performance dashboard"
    >
      Perf: {showDashboard ? 'ON' : 'OFF'}
    </button>
  );
}